/**
 * Native Shoes - Custom Size Selector
 * -----------------------------------------------------------------
 * Author:   Anton Siedlecki
 * Date:     2026-05-27
 * Version:  1.12.1
 * Purpose:  Provides interactivity for the Native Shoes size
 *           selector custom element. Maps button clicks by Shoe Sizes
 *           metaobject handle to Shopify variants through the variant
 *           metafield `shoe.sizes`, updates the product form hidden
 *           variant id input, and applies inventory visibility from
 *           Shopify variant availability.
 *
 *           v1.12.1 — deriveSizeMeta() value correction (2026-09-08):
 *           selected_age is now just "Adults"/"Kids" (little-kids and
 *           big-kids both collapse to "Kids", not separate values), and
 *           selected_gender for both kids buckets is "Unisex", not
 *           "Little Kids"/"Big Kids" — that distinction still exists in
 *           selected_size/full_size (which keep the real "Little Kids:
 *           C8" style labels), it just isn't part of age/gender.
 *
 *           v1.12.0 — "Size Click Intention" now publishes the fully
 *           derived/shaped payload (2026-09-08), reversing v1.11.0.
 *           v1.11.0 kept the published event deliberately raw (no
 *           gender/age parsing, no label joining) so that logic would
 *           live only in the downstream Customer Events pixel. Live
 *           testing surfaced the practical problem: any raw-event
 *           inspector ("pixel helper") only ever shows the event as
 *           published — since the raw shape had no selected_gender
 *           field, it looked broken there even though the pixel was
 *           computing it correctly downstream. Reversed per explicit
 *           instruction: trackSizeClickIntention() now derives
 *           selected_size/full_size (via variant.labels, matched to the
 *           clicked handle) and selected_gender/selected_age (via the
 *           new deriveSizeMeta() — direct 1:1 mapping off the handle's
 *           own prefix: mens->Mens/Adults, womens->Womens/Adults,
 *           little-kids->"Little Kids" for BOTH gender and age,
 *           big-kids->"Big Kids" for both — no blank case). The
 *           published event now matches the Klaviyo payload field for
 *           field; the pixel is a near-literal passthrough. See that
 *           method's own comment and NATIVE_THEME_NOTES.md's 2026-09-08
 *           entry for the current pixel-side code.
 *
 *           v1.11.0 — "Size Click Intention" (2026-09-08): a genuine
 *           user size click (not a page-load restore) now publishes a
 *           Shopify custom event via Shopify.analytics.publish(),
 *           porting an SFCC GTM tag that inferred shopping-gender
 *           intent from size clicks for Klaviyo. onSizeClick() gained
 *           an isUserClick option (default true from the real click
 *           listener in bindEvents()); the two programmatic callers —
 *           applyPersistedSize() and applyCarriedOverSize() — pass
 *           isUserClick: false so a restored/carried-over size never
 *           fires this (unchanged in v1.12.0).
 *
 *           v1.10.0 — Removed pass-2 (applyLocationAvailability(),
 *           2026-08-25) — it was live-verified to be actively WRONG, not
 *           just redundant. This whole feature was built on a premise
 *           confirmed backwards via a real debug test on a live product
 *           (apollo-10-bone-white-tapioca-off-white): the v1.1.0 comment
 *           below calls pass 1 (this file's own Liquid-rendered
 *           `available`) "a first-paint approximation" and pass 2 (the
 *           Storefront API fetch, via assets/native-location-
 *           availability.js) the source of "accurate" warehouse-scoped
 *           states. It's the opposite. Liquid's own `variant.available`
 *           already resolves to the visitor's real geo-selected market
 *           with no `@inContext` needed — confirmed live: a size with
 *           16 units at the US warehouse and 0 at Canada's correctly
 *           read `available: false` in Liquid under a Canada session,
 *           matching a direct `@inContext(country: CA)` Storefront API
 *           query exactly. Pass 2's fetch, meanwhile, has never queried
 *           per-location at all (`storeAvailability(locationId:)` isn't
 *           a valid Storefront API argument — see native-location-
 *           availability.js's own v1.1.0 changelog) — it only ever
 *           returned the GLOBAL `availableForSale` (summed across every
 *           warehouse), then OVERWROTE this file's already-correct
 *           per-region pass-1 value with it. Net effect: a Canadian
 *           shopper viewing a size out of stock at CA but in stock at
 *           US would see pass 1 correctly grey it out, then pass 2 would
 *           re-enable it moments later — letting them select and submit
 *           a variant their region has zero real stock of. Deleting pass
 *           2 makes pass 1 (already correct, already the only thing
 *           mounted synchronously) the sole and final source of truth.
 *           assets/native-location-availability.js is deleted entirely
 *           as part of this same fix — see that removal's own note in
 *           NATIVE_THEME_NOTES.md for what replaced its other two
 *           contexts (the OOTB variant picker and PLP collection cards).
 *
 *           v1.9.0 — renderSelection() now dispatches a bubbled
 *           native:size-selection-changed CustomEvent on every
 *           selection change (2026-08-19), so
 *           assets/native-product-cross-links.js can re-render its
 *           sibling-color swatches with an up-to-date per-size-per-
 *           color availability flag without a page reload. This file
 *           has no subscribers of its own — it's a one-way signal for
 *           other components.
 *
 *           v1.8.0 — No more defaulting to a size on page load
 *           (2026-08-12). syncFromExistingSelection() used to treat
 *           whatever variant id was already in the hidden form input as
 *           a real selection — but Shopify populates that input with
 *           product.selected_or_first_available_variant regardless of
 *           whether the shopper actually chose anything, so every fresh
 *           PDP visit looked like a size was already picked. Now gated
 *           on an explicit ?variant= deep link (the same signal
 *           applyPersistedSize() already treats as authoritative);
 *           without it, nothing is pre-selected. When nothing ends up
 *           selected after sync/carry-over/persisted-size, the Add to
 *           Bag button is explicitly forced into a disabled "Select
 *           Size" state (window.variantStrings.selectSize, reusing the
 *           toggle's own locale key) instead of leaving the server-
 *           rendered "Add to Bag" markup — which was enabled against
 *           the same silent default variant. The same fix also corrects
 *           clearSelection() (clicking a selected size again to
 *           deselect it), which was showing "Unavailable" instead.
 *
 *           v1.7.0 — Price updates are now near-instant (2026-08-12).
 *           v1.6.0's live fetch had a visible round-trip delay on every
 *           size click. prefetchPrices() now warms an in-memory (not
 *           persisted anywhere) price-HTML cache for every variant in
 *           the background shortly after mount, via requestIdleCallback
 *           so it never competes with critical-path rendering.
 *           updatePriceDisplay() checks that cache first for an instant
 *           swap; if a given variant's prefetch hasn't landed yet (or
 *           failed), it falls straight through to the same live fetch
 *           v1.6.0 already had — the live path is the deterministic
 *           guarantee, the cache is purely a shortcut when it wins the
 *           race against a click.
 *
 *           v1.6.0 — Selecting a size now updates the visible price
 *           (2026-08-12). Previously only the hidden variant id input
 *           updated, so Add to Bag always charged the correct price but
 *           the on-page price never visually changed — most noticeable
 *           on Kids products where sizes can cross a price point. See
 *           updatePriceDisplay() for the fix (re-fetches this section
 *           for the selected variant, same pattern product-info.js
 *           already uses, rather than reconstructing sale/regular price
 *           markup in JS). Also fixed a dead variant-change pub/sub
 *           publish that could never actually fire (window.PUB_SUB_EVENTS
 *           was always undefined — PUB_SUB_EVENTS is a script-scope
 *           const, not a window property).
 *
 *           v1.5.0 — Selected size now survives a page refresh
 *           (2026-08-10). v1.3.0's ?size= carry-over is a one-time
 *           handoff that strips itself from the URL, so reloading the
 *           destination color's page lost the selection again. Now every
 *           real selection is also saved to sessionStorage keyed by the
 *           product's style group (data-style-handle, i.e.
 *           erp.consolidated_style.value — falls back to the product id
 *           for single-color products with no style group) via
 *           savePersistedSize()/applyPersistedSize(). Restoring the
 *           persisted size runs after both syncFromExistingSelection()
 *           and applyCarriedOverSize(), so it overrides the theme's
 *           default variant selection but yields to an explicit
 *           same-navigation carry-over or a `?variant=` deep link.
 *           Deselecting a size (clicking it again) clears the persisted
 *           entry via clearPersistedSize() so a refresh doesn't resurrect
 *           a size the shopper intentionally cleared.
 *
 *           v1.4.0 — Out-of-stock size button wording (2026-08-10):
 *           aria-label suffix changed from "— out of stock" to
 *           "— Sold out" per user request. Opacity for the out-of-stock
 *           visual state moved from assets/native-size-selector.css.
 *
 *           v1.3.0 — Carry the selected size across a color change
 *           (2026-08-10). Each color is a separate product in this
 *           catalog, so changing color is a full page navigation —
 *           applyCarriedOverSize() reads a ?size= query param (appended
 *           by native-product-cross-links.js onto each sibling color
 *           link at click time) and auto-selects the equivalent size on
 *           the destination product if it exists there, then cleans the
 *           param out of the URL via history.replaceState.
 *
 *           v1.2.0 — Out-of-stock size buttons are now clickable
 *           (2026-08-10). Previously a click on a button with
 *           aria-disabled="true" was blocked entirely in bindEvents(),
 *           so an out-of-stock size could never actually be selected —
 *           onSizeClick()/updateProductForm() never ran, and the Add to
 *           Bag button never got the chance to reflect the sold-out
 *           state for that variant. Removed the click-blocking guard so
 *           selecting an out-of-stock size now correctly drives the Add
 *           to Bag button into its disabled/sold-out state (already-
 *           existing logic in updateProductForm(), previously
 *           unreachable for this case). This is required groundwork for
 *           an eventual Klaviyo "notify me when back in stock" swap on
 *           the Add to Bag button, which needs a real selected-but-
 *           unavailable variant to attach to. applyAvailabilityState()
 *           no longer sets aria-disabled on size buttons (they're
 *           genuinely interactive now, so telling screen readers
 *           otherwise would be inaccurate) — it updates the button's
 *           aria-label to include "— out of stock" instead, so the
 *           state is still announced. The out-of-stock visual treatment
 *           (faded text, diagonal slash) is unchanged.
 *
 *           v1.1.1 — renderSelection() now filters out null entries from
 *           variant.labels before joining for the collapsed header text.
 *           Liquid renders null for a handle with no matching shoe_sizes
 *           metaobject (a genuine data gap) instead of a fabricated
 *           number, so a side with no real/selectable size is omitted
 *           from the header entirely rather than showing a made-up value.
 *           Pairs with snippets/native-size-selector.liquid v1.1.6.
 *
 *           v1.1.0 — Availability is now scoped to the shopper's
 *           warehouse location (W28 for CA, W18 for US).
 *           applyAvailabilityState() is called twice:
 *             1. Synchronously on mount using Liquid-rendered
 *                `available` values as a first-paint approximation.
 *             2. Asynchronously after mount via
 *                NativeShops.locationAvailability.forProduct(),
 *                which fetches storeAvailability(locationId) per
 *                variant from the Storefront API and re-applies
 *                accurate disabled states once resolved.
 *           The shared cache in native-location-availability.js
 *           prevents duplicate Storefront API calls when both the
 *           size selector and OOTB picker are present on the same
 *           page load.
 *
 * Debug:
 *   Enable logging with either:
 *     localStorage.setItem('nativeSizeDebug', '1')
 *   or:
 *     ?native_size_debug=1
 */

(function () {
  'use strict';


  class NativeSizeSelector extends HTMLElement {
    constructor() {
      super();
      this.selectedVariantId = null;
      this.selectedHandles = [];
      this.debug = NativeSizeSelector.isDebugEnabled();
      // In-memory only, this page-load only — never written to session/
      // localStorage. See prefetchPrices()/updatePriceDisplay() below.
      this.priceHtmlCache = new Map();
      // Keyed by bucket ('little'/'big'), not variant id — every variant
      // in a bucket shares the exact same gallery HTML, so there are only
      // ever 2 possible cache entries. See prefetchGalleryMedia()/
      // updateGalleryMedia() below.
      this.galleryHtmlCache = new Map();
      this.currentGalleryBucket = null;
    }


    connectedCallback() {
      this.variants = this.parseVariants();
      this.styleKey = NativeSizeSelector.norm(this.dataset.styleHandle) || `product-${this.dataset.productId}`;
      this.toggleBtn = this.querySelector('.native-size-selector__toggle');
      this.toggleLabelEl = this.querySelector('.native-size-selector__toggle-label');
      this.defaultLabel = this.toggleLabelEl?.dataset.defaultLabel || 'Select a size';
      this.panel = this.querySelector('.native-size-selector__panel');
      this.sizeButtons = Array.from(this.querySelectorAll('.native-size-selector__size'));


      this.variants.forEach((v) => {
        v.handlesNorm = (v.handles || []).map((h) => NativeSizeSelector.norm(h));
      });


      this.debugInitialState();

      // First paint: apply availability from Liquid-rendered `available` field.
      // This runs synchronously so buttons have correct states before the
      // Storefront API responds.
      this.applyAvailabilityState(this.buildAvailableHandlesFromLiquid());

      this.bindEvents();
      this.syncFromExistingSelection();

      // v1.3.0 (2026-08-10): carry the shopper's selected size across a
      // color change. Each color is a separate product in this catalog,
      // so "changing color" is a full page navigation — see the
      // corresponding change in native-product-cross-links.js, which
      // appends the currently selected size as a ?size= param onto each
      // sibling color link. Runs after syncFromExistingSelection() so it
      // takes priority over the theme's default variant selection.
      this.applyCarriedOverSize();

      // v1.5.0 (2026-08-10): restore a size the shopper picked earlier in
      // this style group, if a refresh (or a plain revisit) wiped the
      // theme's own selection back to its default variant. Runs after
      // applyCarriedOverSize() — a same-click color-change handoff is a
      // stronger, more specific signal than a remembered selection, and
      // if it already applied, this becomes a no-op via the
      // already-selected guard below.
      this.applyPersistedSize();

      // v1.8.0 (2026-08-12): if none of the above actually selected
      // anything (no ?variant= deep link, no ?size= carry-over, nothing
      // persisted for this style group), force the Add to Bag button
      // into its real "nothing chosen yet" state — disabled, "Select
      // Size" — instead of leaving the server-rendered markup as-is,
      // which showed "Add to Bag" already enabled against Shopify's
      // silent default variant.
      if (!this.selectedVariantId) {
        this.updateProductForm(null);
      }

      // v1.7.0 (2026-08-12): warm the price cache for every size in the
      // background so selecting one is instant instead of waiting on a
      // network round-trip. See prefetchPrices() below.
      this.prefetchPrices();

      // 2026-09-16: same idea for the gallery — see prefetchGalleryMedia()
      // below. Also record which bucket the server already rendered on
      // load, so the first size click doesn't do a pointless same-bucket
      // "swap" (see updateGalleryMedia()'s early-return).
      if (this.selectedVariantId) {
        const initialVariant = this.variants.find((v) => v.id === this.selectedVariantId);
        this.currentGalleryBucket = NativeSizeSelector.bucketFromHandle(initialVariant?.handles?.[0]);
      }
      this.prefetchGalleryMedia();
    }


    // -----------------------------------------------------------------
    // Persisted size — survives a page refresh within the same style
    // group (or the same product, if it has no style group).
    // -----------------------------------------------------------------

    static PERSIST_KEY = 'nativeSelectedSize';

    savePersistedSize(handle) {
      try {
        window.sessionStorage?.setItem(
          NativeSizeSelector.PERSIST_KEY,
          JSON.stringify({ styleKey: this.styleKey, sizeHandle: handle })
        );
      } catch (e) {
        // Storage unavailable (private mode, quota, etc.) — non-fatal.
      }
    }

    clearPersistedSize() {
      try {
        const raw = window.sessionStorage?.getItem(NativeSizeSelector.PERSIST_KEY);
        if (!raw) return;
        const stored = JSON.parse(raw);
        if (stored?.styleKey === this.styleKey) {
          window.sessionStorage.removeItem(NativeSizeSelector.PERSIST_KEY);
        }
      } catch (e) {
        // Ignore malformed/unavailable storage.
      }
    }

    applyPersistedSize() {
      // An explicit deep link to a specific variant is a stronger signal
      // than a remembered selection — don't override it.
      if (new URLSearchParams(window.location.search).get('variant')) return;

      let stored;
      try {
        const raw = window.sessionStorage?.getItem(NativeSizeSelector.PERSIST_KEY);
        if (!raw) return;
        stored = JSON.parse(raw);
      } catch (e) {
        return;
      }

      if (!stored || stored.styleKey !== this.styleKey || !stored.sizeHandle) return;

      const needle = NativeSizeSelector.norm(stored.sizeHandle);
      const alreadySelected = this.selectedHandles.some((h) => NativeSizeSelector.norm(h) === needle);
      if (alreadySelected) return;

      const hasMatch = this.sizeButtons.some((btn) => NativeSizeSelector.norm(btn.dataset.sizeHandle) === needle);
      if (!hasMatch) {
        this.log('persisted size not available on this product', { size_handle: stored.sizeHandle });
        return;
      }

      this.log('applying persisted size', { size_handle: stored.sizeHandle });
      this.onSizeClick(stored.sizeHandle, { isUserClick: false });
    }


    applyCarriedOverSize() {
      const params = new URLSearchParams(window.location.search);
      const sizeParam = params.get('size');
      if (!sizeParam) return;

      const needle = NativeSizeSelector.norm(sizeParam);

      // Already selected via syncFromExistingSelection() — nothing to do.
      // (Also avoids onSizeClick()'s toggle-off-if-already-selected
      // behavior, which would otherwise deselect it here.)
      const alreadySelected = this.selectedHandles.some((h) => NativeSizeSelector.norm(h) === needle);
      if (alreadySelected) return;

      const hasMatch = this.sizeButtons.some((btn) => NativeSizeSelector.norm(btn.dataset.sizeHandle) === needle);
      if (!hasMatch) {
        this.log('carried-over size not available on this product', { size_param: sizeParam });
        return;
      }

      this.log('applying carried-over size', { size_param: sizeParam });
      this.onSizeClick(sizeParam, { isUserClick: false });

      // Keep the URL clean — the carry-over is a one-time handoff between
      // pages, not a permanent part of this product's URL.
      if (window.history?.replaceState) {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('size');
        window.history.replaceState({}, '', cleanUrl.toString());
      }
    }


    static isDebugEnabled() {
      const params = new URLSearchParams(window.location.search);
      return (
        params.get('native_size_debug') === '1' ||
        window.localStorage?.getItem('nativeSizeDebug') === '1'
      );
    }


    static norm(handle) {
      return String(handle || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-');
    }


    // 2026-09-16: mirrors the bucket derivation snippets/product-media-
    // gallery.liquid does server-side off the same shoe.sizes handle
    // prefix — lets the gallery-swap code decide "did the bucket actually
    // change?" purely client-side, no server round-trip needed just to
    // find that out.
    static bucketFromHandle(handle) {
      if (!handle) return null;
      const prefix = String(handle).split('-').slice(0, 2).join('-');
      if (prefix === 'little-kids') return 'little';
      if (prefix === 'big-kids') return 'big';
      return null;
    }


    log(label, payload) {
      if (!this.debug) return;
      console.log(`[native-size-selector] ${label}`, payload);
    }


    table(label, rows) {
      if (!this.debug) return;
      console.groupCollapsed(`[native-size-selector] ${label}`);
      console.table(rows);
      console.groupEnd();
    }


    debugInitialState() {
      if (!this.debug) return;


      this.table(
        'variant JSON from Liquid',
        this.variants.map((v) => ({
          id: v.id,
          sku: v.sku,
          title: v.title,
          available: v.available,
          inventory_quantity: v.inventory_quantity,
          inventory_management: v.inventory_management,
          inventory_policy: v.inventory_policy,
          handles_raw: (v.handles || []).join(', '),
          handles_norm: (v.handlesNorm || []).join(', '),
          labels: (v.labels || []).join(' / '),
          options: (v.options || []).join(' / '),
        }))
      );


      this.table(
        'rendered Shoe Size metaobject buttons',
        this.sizeButtons.map((btn) => ({
          button_label: btn.dataset.sizeLabel,
          mo_handle_raw: btn.dataset.sizeHandle,
          mo_handle_norm: NativeSizeSelector.norm(btn.dataset.sizeHandle),
        }))
      );
    }


    // -----------------------------------------------------------------
    // Availability — Liquid-rendered `available` per variant, already
    // resolved to the visitor's real market (see this file's v1.10.0
    // changelog for the live test proving this and why the async
    // "location-scoped" second pass that used to run here was removed —
    // it was overwriting this correct value with a strictly worse,
    // global one).
    // -----------------------------------------------------------------

    buildAvailableHandlesFromLiquid() {
      const availableHandles = new Set();
      this.variants.forEach((v) => {
        if (v.available) {
          v.handlesNorm.forEach((h) => availableHandles.add(h));
        }
      });
      return availableHandles;
    }


    // -----------------------------------------------------------------
    // Shared availability renderer — accepts a Set of available handles
    // -----------------------------------------------------------------

    applyAvailabilityState(availableHandles) {
      const rows = [];


      this.sizeButtons.forEach((btn) => {
        const handleRaw = btn.dataset.sizeHandle;
        const handleNorm = NativeSizeSelector.norm(handleRaw);
        const matchingVariants = this.variants.filter((v) => v.handlesNorm.includes(handleNorm));
        const available = availableHandles.has(handleNorm);


        btn.classList.toggle('native-size-selector__size--disabled', !available);


        // v1.2.0 (2026-08-10): out-of-stock size buttons stay clickable —
        // selecting one now correctly drives the Add to Bag button into
        // its sold-out/disabled state (see updateProductForm()) instead
        // of the button itself blocking the click. This is required for
        // an eventual Klaviyo "notify me when back in stock" swap on the
        // Add to Bag button, which needs the shopper to be able to select
        // an out-of-stock size in the first place. aria-disabled is no
        // longer set here since the button is genuinely interactive —
        // leaving it would tell screen readers not to interact with a
        // button sighted users can click. The accessible name is updated
        // instead so the out-of-stock state is still announced.
        const baseLabel = btn.dataset.sizeLabel || btn.textContent.trim();
        btn.removeAttribute('aria-disabled');
        if (available) {
          btn.setAttribute('aria-label', baseLabel);
        } else {
          btn.setAttribute('aria-label', baseLabel + ' — Sold out');
        }


        rows.push({
          button_label: btn.dataset.sizeLabel,
          mo_handle_raw: handleRaw,
          mo_handle_norm: handleNorm,
          enabled_by_js: available,
          matching_variant_ids: matchingVariants.map((v) => v.id).join(', '),
          matching_skus: matchingVariants.map((v) => v.sku).join(', '),
          matching_available: matchingVariants.map((v) => v.available).join(', '),
          matching_inventory_qty: matchingVariants.map((v) => v.inventory_quantity).join(', '),
          matching_inventory_policy: matchingVariants.map((v) => v.inventory_policy).join(', '),
          matching_inventory_management: matchingVariants.map((v) => v.inventory_management).join(', '),
        });
      });


      this.table('button availability mapping', rows);
    }


    parseVariants() {
      const script = this.querySelector('script[data-native-size-variants]');
      if (!script) return [];


      try {
        return JSON.parse(script.textContent.trim());
      } catch (e) {
        console.error('[native-size-selector] failed to parse variants JSON', e);
        console.log('[native-size-selector] raw JSON content:', script.textContent);
        return [];
      }
    }


    bindEvents() {
      this.toggleBtn?.addEventListener('click', () => this.togglePanel());


      // v1.2.0 (2026-08-10): out-of-stock size buttons are intentionally
      // still clickable — see the comment in applyAvailabilityState()
      // for why (Klaviyo back-in-stock notify flow needs to be able to
      // select an unavailable size).
      this.sizeButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.onSizeClick(btn.dataset.sizeHandle);
        });
      });


      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.closePanel();
      });


      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closePanel();
      });
    }


    togglePanel() {
      const open = this.toggleBtn.getAttribute('aria-expanded') === 'true';
      open ? this.closePanel() : this.openPanel();
    }


    openPanel() {
      this.toggleBtn.setAttribute('aria-expanded', 'true');
      this.panel.hidden = false;
      this.classList.add('native-size-selector--open');
    }


    closePanel() {
      this.toggleBtn.setAttribute('aria-expanded', 'false');
      this.panel.hidden = true;
      this.classList.remove('native-size-selector--open');
    }


    onSizeClick(handle, { isUserClick = true } = {}) {
      const needle = NativeSizeSelector.norm(handle);
      const selectedNorm = this.selectedHandles.map((h) => NativeSizeSelector.norm(h));


      this.log('size clicked', {
        clicked_handle_raw: handle,
        clicked_handle_norm: needle,
        currently_selected_raw: this.selectedHandles,
        currently_selected_norm: selectedNorm,
      });


      if (selectedNorm.includes(needle)) {
        this.log('deselecting active size or pair', {
          clicked_handle_raw: handle,
          selected_variant_id: this.selectedVariantId,
          selected_handles: this.selectedHandles,
        });


        this.clearPersistedSize();
        this.clearSelection();
        return;
      }


      const variant = this.findVariantByHandle(handle);


      this.log('matched variant from clicked size', {
        clicked_handle_raw: handle,
        clicked_handle_norm: needle,
        matched_variant: variant,
      });


      if (!variant) return;


      // v1.11.0: only a genuine user click counts as "intent" — the two
      // programmatic callers (applyPersistedSize(), applyCarriedOverSize())
      // pass isUserClick: false so a page-load restore never fires this.
      if (isUserClick) {
        this.trackSizeClickIntention(handle, variant);
      }


      this.selectedVariantId = variant.id;
      this.selectedHandles = variant.handles.slice();


      this.renderSelection();
      this.updateProductForm(variant);
      this.savePersistedSize(handle);
    }


    // -----------------------------------------------------------------
    // "Size Click Intention" — publishes a fully-shaped Shopify custom
    // event on every genuine size selection, ported from an SFCC GTM tag
    // that inferred shopping-gender intent from size clicks and pushed
    // it to Klaviyo. v1.11.0 originally kept this raw (no derivation) so
    // all shaping lived downstream in the Customer Events pixel — REVERSED
    // per explicit instruction (2026-09-08, after live testing): the
    // published event now already carries the exact fields sent to
    // Klaviyo, so any raw-event inspector shows the same outcome as
    // what's delivered, no downstream computation to trust. The pixel is
    // now a near-literal passthrough of this payload — see
    // NATIVE_THEME_NOTES.md's 2026-09-08 entry for the pixel-side code.
    // -----------------------------------------------------------------

    static deriveSizeMeta(handle) {
      const parts = NativeSizeSelector.norm(handle).split('-');
      const firstTwo = parts.slice(0, 2).join('-');
      if (firstTwo === 'little-kids') return { gender: 'Unisex', age: 'Kids' };
      if (firstTwo === 'big-kids') return { gender: 'Unisex', age: 'Kids' };
      if (parts[0] === 'mens') return { gender: 'Mens', age: 'Adults' };
      if (parts[0] === 'womens') return { gender: 'Womens', age: 'Adults' };
      return { gender: '', age: '' };
    }


    trackSizeClickIntention(handle, variant) {
      if (!window.Shopify?.analytics?.publish) {
        this.log('Shopify.analytics.publish unavailable — skipping size_click_intention', {});
        return;
      }


      const needle = NativeSizeSelector.norm(handle);
      const labelIndex = (variant.handlesNorm || []).indexOf(needle);
      const selectedSize = labelIndex > -1 ? variant.labels[labelIndex] : handle;
      const fullSize = (variant.labels || []).filter(Boolean).join(' / ') || (variant.handles || []).join(' / ');
      const meta = NativeSizeSelector.deriveSizeMeta(handle);
      // Real numeric comparison, not a truthy check — Liquid's own
      // compare_at_price can be truthy while mirroring price exactly
      // (no real markdown), the same trap already fixed once in this
      // project for collection-card Sale badges (see
      // NATIVE_THEME_NOTES.md, 2026-08-07).
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;


      const payload = {
        full_size: fullSize,
        selected_size: selectedSize,
        selected_age: meta.age,
        selected_gender: meta.gender,
        in_stock: !!variant.available,
        country: window.NativeShops?.config?.countryCode || '',
        sku: variant.sku,
        style_id: this.dataset.styleId || '',
        style_name: this.dataset.styleName || '',
        color_id: this.dataset.colorId || '',
        color_name: this.dataset.colorName || '',
        shoe_type: this.dataset.shoeType || '',
        price_msrp: onSale ? variant.compare_at_price : variant.price,
        price_sale: onSale ? variant.price : undefined,
        currency: this.dataset.currency || '',
      };


      this.log('publishing size_click_intention', payload);


      try {
        window.Shopify.analytics.publish('size_click_intention', payload);
      } catch (e) {
        this.log('size_click_intention publish failed', e);
      }
    }


    findVariantByHandle(handle) {
      const needle = NativeSizeSelector.norm(handle);
      const matches = this.variants.filter((v) => v.handlesNorm.includes(needle));


      this.table(
        `variant matches for ${handle}`,
        matches.map((v) => ({
          id: v.id,
          sku: v.sku,
          title: v.title,
          available: v.available,
          inventory_quantity: v.inventory_quantity,
          inventory_management: v.inventory_management,
          inventory_policy: v.inventory_policy,
          handles_raw: (v.handles || []).join(', '),
          handles_norm: (v.handlesNorm || []).join(', '),
        }))
      );


      if (matches.length === 0) return null;
      return matches.find((v) => v.available) || matches[0];
    }


    clearSelection() {
      this.selectedVariantId = null;
      this.selectedHandles = [];
      this.renderSelection();
      this.updateProductForm(null);
    }


    renderSelection() {
      const selectedNorm = this.selectedHandles.map((h) => NativeSizeSelector.norm(h));


      this.sizeButtons.forEach((btn) => {
        const selected = selectedNorm.includes(NativeSizeSelector.norm(btn.dataset.sizeHandle));
        btn.classList.toggle('native-size-selector__size--selected', selected);
        btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });


      if (this.selectedHandles.length === 0) {
        this.toggleLabelEl.textContent = this.defaultLabel;
        this.classList.remove('native-size-selector--has-selection');
      } else {
        const variant = this.variants.find((v) => v.id === this.selectedVariantId);
        const labels = variant?.labels?.length ? variant.labels : this.selectedHandles;
        // v1.1.6: a side with no real matching size (e.g. a Womens size
        // that doesn't exist as a metaobject at all) is rendered as null
        // by Liquid rather than a fabricated number — filter those out so
        // the header only ever shows sizes that actually, visibly exist.
        const visibleLabels = labels.filter(Boolean);
        this.toggleLabelEl.textContent = visibleLabels.length
          ? visibleLabels.join(' / ')
          : this.selectedHandles.join(' / ');
        this.classList.add('native-size-selector--has-selection');
      }

      // 2026-08-19: lets native-product-cross-links.js re-render its
      // sibling-color swatches with an up-to-date per-size-per-color
      // availability flag any time the selection changes (select, switch,
      // or clear) — see isSizeUnavailable() there. Bubbles so a plain
      // document listener catches it regardless of DOM nesting.
      this.dispatchEvent(new CustomEvent('native:size-selection-changed', {
        bubbles: true,
        detail: { selectedHandles: this.selectedHandles.slice(), styleKey: this.styleKey }
      }));
    }


    updateProductForm(variant) {
      const formId = this.dataset.formId;
      const form = formId
        ? document.getElementById(formId)
        : document.querySelector('form[action*="/cart/add"]');


      if (!form) {
        this.log('product form not found', { formId });
        return;
      }


      const idInput = form.querySelector('input[name="id"]');


      if (!idInput) {
        this.log('variant id input not found', { formId });
        return;
      }


      const oldValue = idInput.value;
      const newValue = variant ? String(variant.id) : '';


      this.log('updating product form variant id', {
        old_value: oldValue,
        new_value: newValue,
        variant,
      });


      if (idInput.value !== newValue) {
        idInput.value = newValue;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }


      const submit = form.querySelector('[type="submit"]');
      const submitText = submit?.querySelector('span');


      if (submit) {
        if (!variant) {
          // v1.8.0 (2026-08-12): this branch means "no size selected" —
          // updateProductForm(null) is only ever called from
          // clearSelection() (an explicit deselect) or once at mount if
          // nothing ended up selected. It was showing the "Unavailable"
          // string, which is wrong for both cases — neither one means the
          // product is unavailable, they mean the shopper hasn't picked a
          // size yet. Reuses the same locale key as the toggle's own
          // default label so the two stay consistent with each other.
          submit.setAttribute('disabled', 'disabled');
          if (submitText && window.variantStrings?.selectSize) {
            submitText.textContent = window.variantStrings.selectSize;
          }
        } else if (!variant.available) {
          submit.setAttribute('disabled', 'disabled');
          if (submitText && window.variantStrings?.soldOut) {
            submitText.textContent = window.variantStrings.soldOut;
          }
        } else {
          submit.removeAttribute('disabled');
          if (submitText && window.variantStrings?.addToCart) {
            submitText.textContent = window.variantStrings.addToCart;
          }
        }
      }


      // v1.6.0 (2026-08-12): PUB_SUB_EVENTS is a top-level `const` in
      // assets/constants.js — a classic-script const/let never becomes a
      // property of window, only window.publish (a function DECLARATION)
      // does. `window.PUB_SUB_EVENTS` was therefore always undefined and
      // this whole block was dead code — no variant-change event has ever
      // actually fired from this component. Reference the bare identifier
      // instead (still guarded, in case some future page doesn't load
      // constants.js at all).
      if (variant && typeof window.publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        window.publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.dataset.section,
            html: null,
            variant: {
              id: variant.id,
              available: variant.available,
              title: variant.title,
            },
          },
        });
      }

      this.updatePriceDisplay(variant);
      this.updateGalleryMedia(variant);
    }


    // -----------------------------------------------------------------
    // Price display — the theme's stock variant picker updates
    // #price-{section} by re-fetching this section's HTML for the
    // selected variant and swapping it in (see assets/product-info.js
    // ProductInfo.handleUpdateProductInfo / updateSourceFromDestination).
    // This custom size selector sits outside that pipeline entirely — it
    // only ever set the hidden variant id input, so Add to Bag charged
    // the right price but the on-page price never visually updated
    // (most noticeable on Kids products, where sizes can cross a price
    // point). Reuses the exact same `?variant=<id>&section_id=<section>`
    // fetch already used elsewhere in product-info.js (fetchQuantityRules)
    // rather than reconstructing price/sale markup client-side — price.liquid's
    // sale-vs-regular toggle is a server-rendered CSS class swap
    // (.price--on-sale), not something safe to patch from bare JSON
    // variant fields.
    //
    // v1.7.0 (2026-08-12): the live fetch above has a visible round-trip
    // delay. this.priceHtmlCache is an in-memory, this-page-load-only map
    // (nothing persisted to session/localStorage — cleared the moment the
    // page unloads, not a durable cache) pre-populated in the background
    // for every variant shortly after mount via prefetchPrices(). A size
    // click checks the cache first for an instant swap; if that variant's
    // prefetch hasn't landed yet (or failed), it falls straight through
    // to the same live fetch as before, so a click is never blocked
    // waiting on the background prefetch — the live path is the
    // deterministic guarantee, the cache is purely a shortcut when it
    // wins the race.
    // -----------------------------------------------------------------

    fetchPriceHtml(variantId, signal) {
      const sectionId = this.dataset.section;
      const productUrl = this.dataset.productUrl || window.location.pathname;

      return fetch(`${productUrl}?variant=${variantId}&section_id=${sectionId}`, { signal })
        .then((response) => response.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const freshPrice = doc.getElementById(`price-${sectionId}`);
          return freshPrice ? freshPrice.innerHTML : null;
        });
    }

    prefetchPrices() {
      const run = () => {
        this.variants.forEach((v) => {
          if (this.priceHtmlCache.has(v.id)) return;
          this.fetchPriceHtml(v.id)
            .then((priceHtml) => {
              if (priceHtml) this.priceHtmlCache.set(v.id, priceHtml);
            })
            .catch(() => {
              // Silent — this is a background best-effort warm-up. A size
              // click that lands on this variant just falls through to
              // the live fetch in updatePriceDisplay() as usual.
            });
        });
      };

      // Deferred via requestIdleCallback (with a setTimeout fallback for
      // Safari, which doesn't implement it) so this background warm-up
      // never competes with anything on the critical rendering path.
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run);
      } else {
        setTimeout(run, 200);
      }
    }

    updatePriceDisplay(variant) {
      const sectionId = this.dataset.section;
      const priceEl = sectionId && document.getElementById(`price-${sectionId}`);
      if (!variant || !priceEl) return;

      const cached = this.priceHtmlCache.get(variant.id);
      if (cached) {
        priceEl.innerHTML = cached;
        this.log('price display updated from prefetch cache', { variant_id: variant.id });
        return;
      }

      this.priceFetchAbortController?.abort();
      this.priceFetchAbortController = new AbortController();

      this.fetchPriceHtml(variant.id, this.priceFetchAbortController.signal)
        .then((priceHtml) => {
          if (priceHtml) {
            priceEl.innerHTML = priceHtml;
            this.priceHtmlCache.set(variant.id, priceHtml);
            this.log('price display updated from live fetch', { variant_id: variant.id });
          }
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            this.log('price refresh failed — Add to Bag still uses the correct variant', error);
          }
        });
    }

    // -----------------------------------------------------------------
    // Gallery media — for the handful of kids products with a
    // shoe.gallery_little_kids / shoe.gallery_big_kids metafield pair
    // (see snippets/product-media-gallery.liquid), the PDP gallery shows
    // a different photo set depending on which size bucket is selected.
    // Size changes never reach that gallery through Shopify's native
    // pipeline (assets/product-info.js only listens for
    // optionValueSelectionChange, which this custom size selector never
    // publishes — see snippets/product-variant-picker.liquid's own note
    // on why Size is rendered outside that pipeline entirely). Reuses
    // the exact same `?variant=<id>&section_id=<section>` fetch shape as
    // fetchPriceHtml() above, just pulling out #pageGallery-<section>
    // instead of #price-<section>. Gated on data-size-media-active so
    // every other product (where both metafields are blank) never pays
    // for any of this.
    //
    // v2 (2026-09-16): the first version fetched on every single size
    // click, which was clearly laggy in practice — a real round trip plus
    // the browser having to download 6-8 brand-new images it had never
    // seen before. Two things actually cause the content to change
    // though, and neither is "which exact size was clicked": (1) the
    // BUCKET (little vs. big) — every variant within a bucket renders the
    // identical gallery HTML, so re-fetching on every click inside the
    // same bucket was pure waste; (2) whether that bucket's HTML/images
    // have already been fetched once this page view. Fixed both:
    // - currentGalleryBucket tracks what's actually on screen; a click
    //   that stays within the same bucket now short-circuits immediately,
    //   no fetch, no DOM swap, nothing to wait on.
    // - galleryHtmlCache is keyed by bucket (only 2 possible entries
    //   ever), not by variant id — once a bucket's HTML has been fetched
    //   once, every later crossing into it is an instant cache hit.
    // - prefetchGalleryMedia() (mirroring prefetchPrices() below) warms
    //   BOTH buckets' HTML *and* preloads their actual <img> bytes in the
    //   background shortly after mount, so even the FIRST bucket crossing
    //   is typically already-cached by the time a shopper clicks.
    // -----------------------------------------------------------------

    updateGalleryMedia(variant) {
      if (this.dataset.sizeMediaActive !== 'true') return;
      if (!variant || !variant.available) return;

      const newBucket = NativeSizeSelector.bucketFromHandle(variant.handles && variant.handles[0]);
      if (!newBucket || newBucket === this.currentGalleryBucket) return;

      const galDestination = document.getElementById(`pageGallery-${this.dataset.section}`);
      if (!galDestination) return;

      const applyGalleryHtml = (html) => {
        galDestination.innerHTML = html;
        this.currentGalleryBucket = newBucket;
        // Same post-swap housekeeping assets/product-info.js runs after
        // its own gallery swap (updateMedia()) — arrows/thumbnail-scroll
        // state is bound to the DOM nodes just replaced.
        if (typeof sliderArrows === 'function') sliderArrows();
        if (typeof thumbnailsScroll === 'function') thumbnailsScroll();
      };

      const cached = this.galleryHtmlCache.get(newBucket);
      if (cached) {
        applyGalleryHtml(cached);
        this.log('gallery media updated from cache', { bucket: newBucket });
        return;
      }

      this.galleryFetchAbortController?.abort();
      this.galleryFetchAbortController = new AbortController();

      this.fetchGalleryHtml(variant.id, this.galleryFetchAbortController.signal)
        .then((html) => {
          if (!html) return;
          this.galleryHtmlCache.set(newBucket, html);
          applyGalleryHtml(html);
          this.log('gallery media updated from live fetch', { bucket: newBucket, variant_id: variant.id });
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            this.log('gallery media refresh failed — size selection still applied normally', error);
          }
        });
    }

    fetchGalleryHtml(variantId, signal) {
      const sectionId = this.dataset.section;
      const productUrl = this.dataset.productUrl || window.location.pathname;

      return fetch(`${productUrl}?variant=${variantId}&section_id=${sectionId}`, { signal })
        .then((response) => response.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const galSource = doc.getElementById(`pageGallery-${sectionId}`);
          return galSource ? galSource.innerHTML : null;
        });
    }

    // Kicks off in the background shortly after mount, same idle-callback
    // pattern as prefetchPrices() — fetches one representative variant's
    // gallery HTML per bucket (any variant in the bucket does, since the
    // HTML is identical across the whole bucket) and also preloads the
    // actual image bytes via warmGalleryImages(), so the browser already
    // has them cached before a shopper ever clicks into that bucket.
    prefetchGalleryMedia() {
      if (this.dataset.sizeMediaActive !== 'true') return;

      const run = () => {
        ['little', 'big'].forEach((bucket) => {
          if (this.galleryHtmlCache.has(bucket)) return;
          const variant = this.variants.find(
            (v) => NativeSizeSelector.bucketFromHandle(v.handles && v.handles[0]) === bucket
          );
          if (!variant) return;

          this.fetchGalleryHtml(variant.id)
            .then((html) => {
              if (!html) return;
              this.galleryHtmlCache.set(bucket, html);
              this.warmGalleryImages(html);
              this.log('gallery media prefetched', { bucket });
            })
            .catch(() => {
              // Silent — background best-effort warm-up, same as
              // prefetchPrices(). A click that lands here before this
              // resolves just falls through to the live fetch as usual.
            });
        });
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run);
      } else {
        setTimeout(run, 200);
      }
    }

    // Forces the browser to start downloading a bucket's actual image
    // files before it's ever displayed, so applying the cached HTML later
    // paints already-decoded images instead of triggering a fresh
    // download at swap time — the second, less obvious half of the
    // perceived delay a network fetch alone doesn't fix.
    warmGalleryImages(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc.querySelectorAll('img[src]').forEach((img) => {
        new Image().src = img.src;
      });
      doc.querySelectorAll('[srcset]').forEach((el) => {
        const firstUrl = (el.getAttribute('srcset') || '').split(',')[0].trim().split(/\s+/)[0];
        if (firstUrl) new Image().src = firstUrl;
      });
    }


    syncFromExistingSelection() {
      // v1.8.0 (2026-08-12): only trust the hidden variant id input as a
      // real selection if the shopper actually arrived via an explicit
      // ?variant= deep link (e.g. a marketing email pointing at one
      // specific size) — the same signal applyPersistedSize() already
      // treats as authoritative. Without that param, the hidden input
      // still holds Shopify's own silent default
      // (product.selected_or_first_available_variant), and treating that
      // as a real selection made every fresh PDP visit look like a size
      // was already chosen when the shopper hadn't picked anything yet.
      if (!new URLSearchParams(window.location.search).has('variant')) return;

      const formId = this.dataset.formId;
      const form = formId
        ? document.getElementById(formId)
        : document.querySelector('form[action*="/cart/add"]');


      const idInput = form?.querySelector('input[name="id"]');
      const currentId = idInput?.value ? Number(idInput.value) : null;


      this.log('syncing from existing selection', {
        formId,
        current_variant_id_input_value: idInput?.value,
        current_variant_id_number: currentId,
      });


      if (!currentId) return;


      const variant = this.variants.find((v) => v.id === currentId);
      if (!variant) return;


      this.selectedVariantId = variant.id;
      this.selectedHandles = variant.handles.slice();
      this.renderSelection();
    }
  }


  if (!customElements.get('native-size-selector')) {
    customElements.define('native-size-selector', NativeSizeSelector);
  }
})();